import { useId } from 'react'
import PropTypes from 'prop-types'

/**
 * A labelled form field: the label above (Barlow medium, soft ink), a
 * 48px bordered control with 8px corners and a rule-blue focus ring.
 * Renders an input, or a select when `options` is given. `prefix` prints
 * inside the field before the value ("$"); `suffix` prints after the field
 * ("per month"). `layout="inline"` sets the label beside the field on one
 * line instead of above it.
 */
export default function Blank({
  label,
  options,
  prefix,
  suffix,
  layout = 'stacked',
  className = '',
  id: idProp,
  ...rest
}) {
  const autoId = useId()
  const id = idProp || autoId
  const pad = prefix ? 'pl-8' : ''
  const field = options ? (
    <select id={id} className={`blank-field pr-10 truncate ${pad}`} {...rest}>
      {options.map(opt =>
        typeof opt === 'string' ? (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ) : (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        )
      )}
    </select>
  ) : (
    <input id={id} className={`blank-field ${pad}`} {...rest} />
  )

  const inline = layout === 'inline'

  return (
    <div
      className={`min-w-0 ${inline ? 'flex items-center gap-4' : ''} ${className}`}
    >
      <label
        htmlFor={id}
        className={`font-sans text-sm font-medium text-ink-soft ${
          inline ? 'shrink-0' : 'block mb-1.5'
        }`}
      >
        {label}
      </label>
      <div className="flex flex-1 items-center gap-3 min-w-0">
        <div className="relative flex-1 min-w-0">
          {prefix && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 font-sans font-medium text-ink-mute"
            >
              {prefix}
            </span>
          )}
          {field}
          {options && (
            <svg
              aria-hidden="true"
              viewBox="0 0 12 8"
              className="pointer-events-none absolute right-4 top-1/2 h-2 w-3 -translate-y-1/2 text-ink-mute"
            >
              <path
                d="M1 1l5 5 5-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          )}
        </div>
        {suffix && (
          <span
            aria-hidden="true"
            className="font-sans text-sm text-ink-mute whitespace-nowrap"
          >
            {suffix}
          </span>
        )}
      </div>
    </div>
  )
}

Blank.propTypes = {
  label: PropTypes.node.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        label: PropTypes.node.isRequired,
      }),
    ])
  ),
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  layout: PropTypes.oneOf(['stacked', 'inline']),
  className: PropTypes.string,
  id: PropTypes.string,
}
